/* eslint-disable react/jsx-curly-newline */
import { useRef, useState, useEffect, ReactNode } from 'react';
import { MdOutlineFileUpload } from 'react-icons/md';
import { FaTrashAlt } from 'react-icons/fa';
import { ReactComponent as ErrorFile } from 'assets/images/upload/errorFile.svg';
import { ReactComponent as ErrorSize } from 'assets/images/upload/errorSize.svg';
import { ReactComponent as ErrorUpload } from 'assets/images/upload/errorUpload.svg';
import { Button } from '@grupoboticario/flora-react';

import environment from 'environments/environment';

import {
  UploadInputWrapper,
  UploadedList,
  UploadedListFile,
  Loading,
} from './styles';
import ModalDeleteUploadedFile from '../ModalDeleteUploadedFile';

export interface FilesCallback {
  idDoc: string;
  file: any;
}
interface IUpload {
  idDoc: string;
  setState?: any;
  required: boolean;
  files?: any;
  dropFiles?: any;
  // typeFiles?: 'image' | 'video' | 'all';
  deleteFile: any;

  callbackFunc: (files: any) => void;
  acceptedFiles?: string[];
  hasLoadingFiles?: boolean;
}

interface MountedFiles {
  idFile?: string;
  data: any;
  index?: string;
  status: string;
  error?: string;
  iconError?: ReactNode;
  icon?: ReactNode;
}

const UploadInput = ({
  idDoc,
  setState = () => {},
  required,
  files,
  dropFiles = [],
  deleteFile,

  callbackFunc,
  hasLoadingFiles = true,
  acceptedFiles = [],
}: IUpload): JSX.Element => {
  const [uploadedFiles, setUploadedFiles] = useState<MountedFiles[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFileToDelete, setModalFileToDelete] = useState();
  const [modalStatus, setModalStatus] = useState(1);

  const fileSizeLimit = Number(environment.fileSizeLimit);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileInputClicked = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onClickDeleteFile = (file: any) => {
    if (file.status === 'error') {
      const newUploadedFiles = [...uploadedFiles];
      const index = newUploadedFiles.findIndex(o => {
        return o.data === file.data;
      });
      if (index !== -1) newUploadedFiles.splice(index, 1);
      setUploadedFiles(newUploadedFiles);
    } else if (!hasLoadingFiles) {
      const newUploadedFiles = [...uploadedFiles];
      const index = newUploadedFiles.findIndex(o => {
        return o.data === file.data;
      });
      if (index !== -1) newUploadedFiles.splice(index, 1);
      setUploadedFiles(newUploadedFiles);
      callbackFunc(
        newUploadedFiles.map(nFile => ({
          idDoc,
          file: nFile.data,
        })),
      );
    } else if (file.status === 'uploaded') {
      setModalOpen(true);
      setModalFileToDelete(file);
    }
  };

  const deleteUploadedFile = async (file: any) => {
    await deleteFile(file.idFile);
    setModalOpen(false);
    setModalStatus(0);
  };

  const verifyFileType = (file: File) => {
    console.log(file.type);
    if (file.size > fileSizeLimit * 10) {
      return 'Tamanho maior que o limite';
    }
    if (
      acceptedFiles.length &&
      !acceptedFiles.find(accepted => file.type === accepted)
    ) {
      return 'Arquivo não permitido';
    }

    return '';
  };

  const handleFiles = async (newFiles: FileList) => {
    let newUploadedFiles: MountedFiles[] = [...uploadedFiles];

    for (const newFile of newFiles) {
      const file = newFile;

      const fileVerification = verifyFileType(file);

      if (fileVerification) {
        newUploadedFiles = [
          ...newUploadedFiles,
          {
            data: file,
            index: file.name + Date.now(),
            status: 'error',
            error: fileVerification,
            iconError:
              fileVerification === 'Arquivo não permitido' ? (
                <ErrorFile />
              ) : (
                <ErrorSize />
              ),
            icon: <FaTrashAlt className="notUploaded" />,
          },
        ];
      } else if (hasLoadingFiles) {
        newUploadedFiles = [
          ...newUploadedFiles,
          {
            data: file,
            index: file.name + Date.now(),
            status: 'loading',
            error: '',
            iconError: null,
            icon: <Loading />,
          },
        ];
      } else {
        newUploadedFiles = [
          ...newUploadedFiles,
          {
            data: file,
            status: 'dontUploaded',
            icon: <FaTrashAlt className="uploaded" />,
          },
        ];
      }
    }
    setUploadedFiles(newUploadedFiles);
    const newLoadingFiles = newUploadedFiles.filter((file: any) =>
      ['loading', 'dontUploaded'].includes(file.status),
    );
    // se existem arquivos validos entre os recebidos:
    if (newLoadingFiles.length > 0) {
      callbackFunc(
        newLoadingFiles.map(file => ({
          idDoc,
          file,
        })),
      );
    } else {
      callbackFunc([]);
    }
  };

  const onChangeFile = () => {
    if (fileInputRef.current?.files) {
      handleFiles(fileInputRef.current.files);
    }
  };

  useEffect(() => {
    const newUploadedFiles: MountedFiles[] = [];
    if (files) {
      files.forEach((file: any) => {
        newUploadedFiles.push({
          data: {
            name: file?.filename,
          },
          index: file.id,
          status: file.error ? 'error' : 'uploaded',
          icon: file.error ? (
            <FaTrashAlt className="notUploaded" />
          ) : (
            <FaTrashAlt className="uploaded" />
          ),
          error: file.error && 'Falha no carregamento',
          iconError: file.error && <ErrorUpload />,
          idFile: file.id,
        });
      });
      setUploadedFiles(newUploadedFiles);
    }
  }, [files]);

  useEffect(() => {
    if (dropFiles?.length > 0) {
      handleFiles(dropFiles);
    }
  }, [dropFiles]);

  useEffect(() => {
    if (required && uploadedFiles.length > 0) {
      if (uploadedFiles.find((file: any) => file.status === 'uploaded')) {
        setState((prevState: any) => {
          const newState = { ...prevState };
          newState[idDoc] = true;
          return newState;
        });
      } else {
        setState((prevState: any) => {
          const newState = { ...prevState };
          newState[idDoc] = false;
          return newState;
        });
      }
    } else if (required && uploadedFiles.length === 0) {
      setState((prevState: any) => {
        const newState = { ...prevState };
        newState[idDoc] = false;
        return newState;
      });
    }
  }, [uploadedFiles]);

  return (
    <UploadInputWrapper>
      <form>
        {uploadedFiles.length === 0 && (
          <>
            <MdOutlineFileUpload className="upload" />
            <span>Arraste e solte os arquivos aqui ou se preferir...</span>
          </>
        )}
        {uploadedFiles.length > 0 && (
          <UploadedList>
            {uploadedFiles.map(file => (
              <UploadedListFile status={file.status} key={file?.index}>
                <div className="name">{file?.data?.name}</div>
                {file.iconError && (
                  <div className="iconError">
                    {file.iconError}
                    <div className="hover">ERRO: {file.error}</div>
                  </div>
                )}
                <div
                  className="icon"
                  onClick={() => onClickDeleteFile(file)}
                  onKeyDown={() => onClickDeleteFile(file)}
                  role="button"
                  tabIndex={0}
                >
                  {file.icon}
                </div>
              </UploadedListFile>
            ))}
          </UploadedList>
        )}

        <Button size="small" type="button" onClick={fileInputClicked}>
          Escolha um arquivo
        </Button>
        <input
          ref={fileInputRef}
          id={idDoc}
          type="file"
          multiple
          onChange={onChangeFile}
        />
      </form>
      <ModalDeleteUploadedFile
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        deleteFile={deleteUploadedFile}
        file={modalFileToDelete}
        status={modalStatus}
        setStatus={setModalStatus}
      />
    </UploadInputWrapper>
  );
};

export default UploadInput;
